import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, DollarSign, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const dollars = (cents) => (cents != null ? (cents / 100).toFixed(2) : '');
const toCents = (d) =>
  d === '' || d == null || isNaN(parseFloat(d)) ? null : Math.round(parseFloat(d) * 100);
const money = (cents) => '$' + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });

// Fee-session quote form: opens PRE-FILLED from the matter type's fee schedule and is
// fully EDITABLE. The schedule range/add-ons/hourly are shown as guidance only — there is
// no floor, ceiling, approval, or override warning. set_quote records any override.
const QuoteModal = ({ isOpen, onClose, intake, onQuoted }) => {
  const [matterTypes, setMatterTypes] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [form, setForm] = useState({
    flat_fee: '', initial_payment: '', monthly_payment: '', payment_day: '',
    scope: '', exclusions: '', fee_agreement: 'upon receipt',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null); setSelectedId(''); setSchedule(null);
    setForm((f) => ({
      ...f, flat_fee: '', initial_payment: '', monthly_payment: '', payment_day: '',
      scope: intake?.practice_area ? `All matters concerning ${intake.practice_area}` : '',
      exclusions: '', fee_agreement: 'upon receipt',
    }));
    const token = localStorage.getItem('token');
    setLoading(true);
    axios.get(`${API_URL}/intake/matter-types`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setMatterTypes(res.data.matter_types || []))
      .catch(() => setError('Could not load matter types'))
      .finally(() => setLoading(false));
  }, [isOpen, intake]);

  const selectMatterType = (id) => {
    setSelectedId(id);
    const fs = matterTypes.find((m) => m.id === id)?.fee_schedule || null;
    setSchedule(fs);
    setForm((f) => ({
      ...f,
      flat_fee: fs?.flat_fee_cents != null ? dollars(fs.flat_fee_cents) : '',
      initial_payment: fs?.initial_payment_cents != null ? dollars(fs.initial_payment_cents) : '',
      monthly_payment: fs?.monthly_payment_cents != null ? dollars(fs.monthly_payment_cents) : '',
      payment_day: fs?.payment_day != null ? String(fs.payment_day) : '',
    }));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!selectedId) { setError('Select a matter type.'); return; }
    if (!form.scope || form.scope.trim().length < 10) { setError('Scope of representation is required (min 10 characters).'); return; }
    const flat = toCents(form.flat_fee);
    if (flat == null) { setError('Enter the flat fee.'); return; }
    setSaving(true); setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/intake/${intake.id}/quote`, {
        matter_type_id: selectedId,
        flat_fee_cents: flat,
        initial_payment_cents: toCents(form.initial_payment),
        monthly_payment_cents: toCents(form.monthly_payment),
        payment_day: form.payment_day ? parseInt(form.payment_day, 10) : null,
        scope_of_representation: form.scope,
        exclusions: form.exclusions || null,
        fee_agreement: form.fee_agreement || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (onQuoted) onQuoted(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Quote failed.');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  const defFlat = schedule?.flat_fee_cents;
  const curFlat = toCents(form.flat_fee);
  const isOverride = defFlat != null && curFlat != null && curFlat !== defFlat;
  const addOns = schedule?.add_ons || [];

  const fld = 'w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500';
  const lbl = 'block text-sm font-medium text-virgo-teal-400 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg bg-virgo-navy-800 rounded-xl border border-virgo-teal-700/40 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-virgo-navy-600">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <DollarSign className="text-cyan-400" size={20} /> Quote {intake?.full_name ? `— ${intake.full_name}` : ''}
          </h2>
          <button onClick={onClose} className="text-virgo-teal-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className={lbl}>Matter type</label>
            <select value={selectedId} onChange={(e) => selectMatterType(e.target.value)} className={fld} disabled={loading}>
              <option value="">{loading ? 'Loading…' : 'Select a matter type…'}</option>
              {matterTypes.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.fee_schedule?.flat_fee_cents != null ? ` — ${money(m.fee_schedule.flat_fee_cents)} default` : ' — no default'}
                </option>
              ))}
            </select>
          </div>

          {schedule && (schedule.fee_min_cents != null || schedule.hourly_rate_cents || addOns.length > 0) && (
            <div className="rounded-lg bg-virgo-navy-700/50 border border-virgo-teal-700/30 p-3 text-xs text-virgo-teal-300 space-y-1">
              <div className="flex items-center gap-1 text-virgo-teal-400 font-medium"><Info size={12} /> Guidance (not enforced — override freely)</div>
              {schedule.fee_min_cents != null && <div>Typical range: {money(schedule.fee_min_cents)}–{money(schedule.fee_max_cents)}</div>}
              {schedule.hourly_rate_cents ? <div>Hourly rate: {money(schedule.hourly_rate_cents)}/hr</div> : null}
              {addOns.map((a, i) => <div key={i}>Add-on line item: {a.label} — {money(a.amount_cents)}</div>)}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Flat fee ($) {isOverride && <span className="text-amber-400 text-xs">· override</span>}</label>
              <input type="number" step="0.01" value={form.flat_fee} onChange={(e) => set('flat_fee', e.target.value)} className={fld} placeholder="3500.00" />
            </div>
            <div>
              <label className={lbl}>Payment day (1–28)</label>
              <input type="number" min="1" max="28" value={form.payment_day} onChange={(e) => set('payment_day', e.target.value)} className={fld} placeholder="optional" />
            </div>
            <div>
              <label className={lbl}>Initial payment ($)</label>
              <input type="number" step="0.01" value={form.initial_payment} onChange={(e) => set('initial_payment', e.target.value)} className={fld} placeholder="optional / staged" />
            </div>
            <div>
              <label className={lbl}>Monthly payment ($)</label>
              <input type="number" step="0.01" value={form.monthly_payment} onChange={(e) => set('monthly_payment', e.target.value)} className={fld} placeholder="optional" />
            </div>
          </div>

          <div>
            <label className={lbl}>Scope of representation</label>
            <textarea rows={3} value={form.scope} onChange={(e) => set('scope', e.target.value)} className={fld + ' resize-none'} placeholder="What this engagement covers…" />
          </div>
          <div>
            <label className={lbl}>Exclusions (optional)</label>
            <input value={form.exclusions} onChange={(e) => set('exclusions', e.target.value)} className={fld} placeholder="e.g. appeals, enforcement" />
          </div>
          <div>
            <label className={lbl}>Payment terms</label>
            <input value={form.fee_agreement} onChange={(e) => set('fee_agreement', e.target.value)} className={fld} placeholder="upon receipt" />
          </div>

          {error && <div className="px-3 py-2 rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 text-sm">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-virgo-navy-600">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-virgo-navy-600 text-virgo-teal-300 hover:text-white">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Save quote'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
